import moment from 'moment/moment';
import { DateTimeKind } from './dateTimeKind';

const canonicalAgencyDateTimeFormat = 'YYYY-MM-DDTHH:mm:ss';
const agencyDateTimeInputFormats = ['YYYY-MM-DD h:mm A', 'YYYY-MM-DD H:mm', 'YYYY-MM-DD HH:mm:ss'];

export class DateTime {
    private constructor(public readonly kind: DateTimeKind, public readonly value: number) {
    }

    public static fromAgencyDateAndTime(dateTime: Date): DateTime {
        return this.fromCanonicalAgencyDateTime(moment.utc(dateTime).format(canonicalAgencyDateTimeFormat));
    }

    public static fromStringWithZoneAsAgency(str: string): DateTime {
        const normalizedInput = (str || '').trim();
        const parsed = moment.parseZone(normalizedInput, moment.ISO_8601, true);
        if (parsed.isValid()) {
            return this.fromCanonicalAgencyDateTime(parsed.format(canonicalAgencyDateTimeFormat));
        }

        return this.fromCanonicalAgencyDateTime(moment.utc(normalizedInput).format(canonicalAgencyDateTimeFormat));
    }

    public static fromStringUtcAsAgency(str: string): DateTime {
        return this.fromStringWithZoneAsAgency(str);
    }

    public static fromStringAgencyDateTime(strDateTime: string): DateTime {
        const normalizedInput = (strDateTime || '').trim();
        const parsed = moment(normalizedInput, agencyDateTimeInputFormats, true);
        if (parsed.isValid()) {
            return this.fromCanonicalAgencyDateTime(parsed.format(canonicalAgencyDateTimeFormat));
        }

        return this.fromCanonicalAgencyDateTime(moment.utc(normalizedInput).format(canonicalAgencyDateTimeFormat));
    }

    public static fromStringAgencyDateAndTime(strDate: string, strTime: string): DateTime {
        return this.fromStringAgencyDateTime(`${strDate} ${strTime}`);
    }

    public static formatAgencyDisplay(str: string | null | undefined, fmt: string): string {
        if (!str) {
            return '';
        }

        return DateTime.fromStringWithZoneAsAgency(str).format(fmt);
    }

    public toDate(): Date {
        return new Date(this.value);
    }

    public format(fmt: string): string {
        return moment.utc(this.value).format(fmt);
    }

    public compareTo(that: DateTime): number {
        if (this.kind !== that.kind) throw new Error('DateTime: can not compare different kinds');
        return this.value - that.value;
    }

    public static compare(a: DateTime, b: DateTime): number {
        if (a.kind !== b.kind) throw new Error('DateTime object must have the same kind');
        return a.value - b.value;
    }

    public toString() {
        return `${moment.utc(this.value).format(canonicalAgencyDateTimeFormat)} (${this.kind})}`;
    }

    private static fromCanonicalAgencyDateTime(value: string): DateTime {
        return new DateTime(DateTimeKind.AGENCY, moment.utc(value, canonicalAgencyDateTimeFormat, true).valueOf());
    }
}
